// NOTE: When you add or change markdown syntax here, also update the Markdown Preview page in the
// management app (apps/management-app/src/screens/MarkdownPreview/syntax-reference.ts). See ./README.md.
import type { Pluggable } from 'unified';

type Node = {
  children?: Node[];
  type: string;
  value?: string;
  data?: {
    hName?: string;
    hProperties?: Record<string, unknown>;
  };
};

export const remarkCitations: Pluggable = () => {
  const MinCitationsToGroup = 5;

  // Helper to collect consecutive citations starting from a given index
  const collectConsecutiveCitations = (
    matches: RegExpMatchArray[],
    startIndex: number
  ): { citations: string[]; nextIndex: number } => {
    const citations: string[] = [matches[startIndex][1]];
    let nextIndex = startIndex + 1;

    while (nextIndex < matches.length) {
      const prevMatch = matches[nextIndex - 1];
      const prevEnd = prevMatch.index + prevMatch[0].length;
      if (matches[nextIndex].index === prevEnd) {
        citations.push(matches[nextIndex][1]);
        nextIndex++;
      } else {
        break;
      }
    }

    return { citations, nextIndex };
  };

  // Helper to create citation nodes
  const createCitationNodes = (
    citations: string[],
    matches: RegExpMatchArray[],
    startIndex: number,
    endIndex: number
  ): Node[] => {
    if (citations.length >= MinCitationsToGroup) {
      return [
        {
          type: 'citations',
          data: {
            hName: 'citations',
            hProperties: {
              values: citations,
            },
          },
        },
      ];
    }

    const nodes: Node[] = [];
    for (let j = startIndex; j < endIndex; j++) {
      nodes.push({
        type: 'citation',
        data: {
          hName: 'citation',
          hProperties: {
            value: matches[j][1],
          },
        },
      });
    }
    return nodes;
  };

  // Helper to add text node if needed
  const addTextNodeIfNeeded = (children: Node[], nodeValue: string, lastIndex: number, start: number) => {
    if (start > lastIndex) {
      children.push({
        type: 'text',
        value: nodeValue.slice(lastIndex, start),
      });
    }
  };

  // Helper to process text node with citations
  const processTextWithCitations = (node: Node): Node[] => {
    const citationPattern = /\^(\d+[a-z]*)\^/g;
    const matches = Array.from(node.value.matchAll(citationPattern));
    const children: Node[] = [];
    let lastIndex = 0;
    let i = 0;

    while (i < matches.length) {
      const match = matches[i];
      const start = match.index;

      addTextNodeIfNeeded(children, node.value, lastIndex, start);

      // Collect consecutive citations
      const { citations, nextIndex } = collectConsecutiveCitations(matches, i);

      // Create appropriate citation nodes
      const citationNodes = createCitationNodes(citations, matches, i, nextIndex);
      children.push(...citationNodes);

      const lastConsumed = matches[nextIndex - 1];
      lastIndex = lastConsumed.index + lastConsumed[0].length;
      i = nextIndex;
    }

    if (lastIndex < node.value.length) {
      children.push({
        type: 'text',
        value: node.value.slice(lastIndex),
      });
    }

    return children;
  };

  return (tree) => {
    const visit = (node: Node) => {
      if (node.type === 'text' && /\^\d+[a-z]*\^/.test(node.value)) {
        return processTextWithCitations(node);
      }
      return [node];
    };

    const transformer = (node: Node) => {
      if (Array.isArray(node.children)) {
        node.children = node.children.flatMap((child) => visit(child).flatMap(transformer));
      }
      return node;
    };

    return transformer(tree);
  };
};
